import { useEffect, useMemo, useState } from "react";
import { BOOST_PACKAGES, type BoostSummary, type Candle, type LaunchSummary } from "@bubblefun/shared";
import { getCandles } from "../lib/api";
import { usd } from "../lib/format";
import { publicMediaUrl } from "../lib/media";
import { Link } from "../lib/router";
import { MiniPriceChart } from "./MiniPriceChart";
import { TokenBadge } from "./TokenBadge";

function countdown(end: string, now: number): string {
  const remaining = Math.max(0, Math.floor((new Date(end).getTime() - now) / 1000));
  const hours = Math.floor(remaining / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  const seconds = remaining % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function BoostCard({ boost, launch, preview, now }: { boost: BoostSummary; launch: LaunchSummary; preview: boolean; now: number }) {
  const [candles, setCandles] = useState<Candle[]>([]);
  const option = BOOST_PACKAGES.find((item) => item.id === boost.packageId) ?? BOOST_PACKAGES[0];
  const image = publicMediaUrl(launch.logoUri);
  const positive = launch.change24h >= 0;
  const progress = Math.max(0, Math.min(100, launch.marketProgress));
  const graduated = launch.status === "milestone" || progress >= 100;

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      if (document.visibilityState === "hidden") return;
      const next = await getCandles(launch.address);
      if (active) setCandles(next);
    };
    void refresh();
    const interval = window.setInterval(() => void refresh(), 30_000);
    return () => { active = false; window.clearInterval(interval); };
  }, [launch.address]);

  return (
    <Link
      href={`/token/${launch.address}`}
      className={`boost-market-card${positive ? " is-up" : " is-down"}`}
      aria-label={`${launch.name}, paid boosted placement, ${usd(launch.marketCapUsd)} market cap, ${positive ? "up" : "down"} ${Math.abs(launch.change24h).toFixed(1)}% in 24 hours`}
    >
      <header className="boost-card-topline">
        <span className="boost-card-badge">{preview ? "Preview boost" : "Boosted"}</span>
        <span className="boost-card-live"><i /> Live <b>{countdown(boost.endsAt, now)}</b></span>
      </header>
      <div className="boost-card-identity">
        <span className="boost-card-logo"><TokenBadge symbol={launch.symbol} size="lg" />{image && <img src={image} alt="" onError={(event) => { event.currentTarget.hidden = true; }} />}</span>
        <span><strong>{launch.name}</strong><small>${launch.symbol}</small></span>
        <em>{option.durationHours}H pass</em>
      </div>
      <dl className="boost-card-metrics">
        <div><dt>Live market cap</dt><dd>{usd(launch.marketCapUsd)}</dd></div>
        <div className={positive ? "is-positive" : "is-negative"}><dt>24h change</dt><dd>{positive ? "+" : ""}{launch.change24h.toFixed(1)}%</dd></div>
      </dl>
      <div className={`boost-card-chart${positive ? " is-positive" : " is-negative"}`}>
        <MiniPriceChart candles={candles} positive={positive} label={`${launch.symbol} recent five-minute market price`} />
        <span>TradingView · 5M</span>
      </div>
      <footer className="boost-card-footer">
        {graduated ? <span className="boost-card-graduated"><i /> Graduated</span> : <span className="boost-card-progress"><span><small>Graduation</small><b>{progress.toFixed(0)}%</b></span><i><b style={{ width: `${progress}%` }} /></i></span>}
        <span className="boost-card-cta">View market <b>↗</b></span>
      </footer>
    </Link>
  );
}

export function BoostHeroSpotlight({ launches, boosts, preview }: { launches: LaunchSummary[]; boosts: BoostSummary[]; preview: boolean }) {
  const [now, setNow] = useState(Date.now());
  const [previewEpoch] = useState(() => Date.now());
  const active = useMemo(() => {
    const paid = boosts
      .filter((boost) => new Date(boost.startsAt).getTime() <= now && new Date(boost.endsAt).getTime() > now)
      .map((boost) => ({ boost, launch: launches.find((launch) => launch.address.toLowerCase() === boost.token.toLowerCase()) }))
      .filter((entry): entry is { boost: BoostSummary; launch: LaunchSummary } => Boolean(entry.launch))
      .slice(0, 3);
    if (!preview || paid.length >= 3) return paid;
    const paidTokens = new Set(paid.map(({ launch }) => launch.address.toLowerCase()));
    const demonstrations = launches
      .filter((launch) => !paidTokens.has(launch.address.toLowerCase()))
      .slice(0, 3 - paid.length)
      .map((launch, index) => {
        const packageId = [3, 2, 1][index] as BoostSummary["packageId"];
        const duration = BOOST_PACKAGES.find((option) => option.id === packageId) ?? BOOST_PACKAGES[0];
        const startsAt = new Date(previewEpoch - 12 * 60_000).toISOString();
        return {
          launch,
          boost: {
            id: `preview-${launch.address}`,
            token: launch.address,
            buyer: launch.creator,
            packageId,
            amountEth: Number(duration.priceEth),
            startsAt,
            endsAt: new Date(previewEpoch + duration.durationHours * 60 * 60_000).toISOString(),
            purchasedAt: startsAt
          } satisfies BoostSummary
        };
      });
    return [...paid, ...demonstrations];
  }, [boosts, launches, now, preview, previewEpoch]);

  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") setNow(Date.now()); };
    const interval = window.setInterval(tick, 1_000);
    document.addEventListener("visibilitychange", tick);
    return () => { window.clearInterval(interval); document.removeEventListener("visibilitychange", tick); };
  }, []);

  return (
    <section className="boosted-market-section" id="boosted-markets" aria-labelledby="boosted-market-title" data-reveal>
      <div className="boosted-section-head">
        <div><span className="eyebrow"><i /> Paid placement</span><h2 id="boosted-market-title">Boosted tokens</h2></div>
        <p>Featured launches and clearly labelled paid placements.</p>
      </div>
      <div className="boosted-market-grid">
        {active.map(({ boost, launch }) => <BoostCard key={boost.id} boost={boost} launch={launch} preview={preview} now={now} />)}
        {!active.length && <div className="boost-market-empty"><span>Placement available</span><h3>Put a live market here.</h3><p>Boost packages start at 0.03 ETH for one hour.</p></div>}
      </div>
      <p className="boosted-disclosure">Paid visibility · not an endorsement · no guarantee of volume, price movement, or graduation</p>
    </section>
  );
}
